const ATTACHMENTS_URL = '/api/attachments';

/** Matches AttachmentController::MAX_FILE_SIZE_KB (20 MB). */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** Abort hanging uploads. Large booklet assets may take a while on Cloud Run. */
const UPLOAD_TIMEOUT_MS = 180000;

/**
 * Upload a file to the Laravel local storage via the AttachmentController.
 *
 * Replaces the legacy Arc JSON-RPC `open.File.update` call.
 *
 * Prefer passing `file` (a browser File/Blob) when available — avoids a
 * FileReader base64 round-trip that balloons memory for multi-MB assets.
 *
 * @param {object} params
 * @param {string} params.fileName    - The filename (with any timestamp suffix already applied).
 * @param {string} [params.base64File] - A data URL from FileReader, e.g. "data:image/jpeg;base64,..."
 * @param {Blob|File} [params.file]   - Prefer this over base64File when available
 * @param {string} [params.folder]    - Storage subfolder, e.g. "team/picture"  (lowercase — UploadFile.js)
 * @param {string} [params.FOLDER]    - Same field but uppercase — CRUDProposal.js passes this key
 *
 * Returns a promise resolving to the same envelope shape all callers expect:
 *   { data: { responses: [{ success, data: { path, url }, message }] } }
 */
export async function uploadFile({ fileName, base64File, file, folder, FOLDER }) {
    const resolvedFolder = folder != null ? folder : (FOLDER != null ? FOLDER : '');

    const blob = file != null ? file : base64ToBlob(base64File);

    if (blob.size > MAX_UPLOAD_BYTES) {
        return {
            data: {
                responses: [{
                    success: false,
                    message: `File is too large (max ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB).`,
                    data:    null,
                }],
            },
        };
    }

    const form = new FormData();
    form.append('file', blob, fileName || (file && file.name) || 'upload.bin');
    form.append('folder', resolvedFolder);

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
        ? setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS)
        : null;

    let response;
    try {
        response = await fetch(ATTACHMENTS_URL, {
            method: 'POST',
            credentials: 'include',
            headers: { Accept: 'application/json' },
            body: form,
            signal: controller ? controller.signal : undefined,
        });
    } catch (networkError) {
        if (timeoutId) clearTimeout(timeoutId);
        if (networkError && networkError.name === 'AbortError') {
            return {
                data: {
                    responses: [{
                        success: false,
                        message: 'Upload timed out. Please try a smaller file or try again.',
                        data:    null,
                    }],
                },
            };
        }
        // Network-level failure (no connection, DNS, etc.) — propagates to caller's .catch()
        throw networkError;
    }

    if (timeoutId) clearTimeout(timeoutId);

    let json;
    try {
        json = await response.json();
    } catch (e) {
        json = {};
    }

    // Normalize both success and HTTP-error responses into the Arc-style envelope
    // that every upload consumer (UploadFile.js, EditorWrapper.js, CRUDProposal.js) expects.
    if (!response.ok) {
        const validationMsg = json.errors && json.errors.file
            ? json.errors.file[0]
            : null;
        return {
            data: {
                responses: [{
                    success: false,
                    message: validationMsg || json.message || `Upload failed (HTTP ${response.status})`,
                    data:    null,
                }],
            },
        };
    }

    return {
        data: {
            responses: [{
                success: json.success,
                message: json.message != null ? json.message : null,
                data:    json.data,
            }],
        },
    };
}

/**
 * Delete a file from Laravel local storage via the AttachmentController.
 *
 * Replaces the legacy Arc JSON-RPC `open.File.purge` call.
 * The server-side delete is idempotent — succeeds silently if the file is already gone.
 *
 * @param {object} params
 * @param {string} params.fileName  - The bare filename, e.g. "photo_123.jpg"
 * @param {string} [params.folder]  - Lowercase folder key — used by UploadFile.js
 * @param {string} [params.FOLDER]  - Uppercase folder key — used by CRUDProposal.js
 *
 * Returns a promise resolving to { success: true } or { success: false, message: "..." }
 */
export async function removeFile({ fileName, folder, FOLDER }) {
    const resolvedFolder = folder != null ? folder : (FOLDER != null ? FOLDER : '');
    const path = resolvedFolder ? `${resolvedFolder}/${fileName}` : fileName;

    let response;
    try {
        response = await fetch(ATTACHMENTS_URL, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept':       'application/json',
            },
            body: JSON.stringify({ path }),
        });
    } catch (networkError) {
        return { success: false, message: 'Network error during file removal.' };
    }

    try {
        return await response.json();
    } catch (e) {
        return { success: response.ok };
    }
}

// -----------------------------------------------------------------------------
// Internal helper
// -----------------------------------------------------------------------------

/**
 * Convert a base64 data URL (produced by FileReader.readAsDataURL) into a Blob
 * suitable for inclusion in a FormData multipart request.
 *
 * @param {string} dataUrl - e.g. "data:image/jpeg;base64,/9j/4AAQ..."
 * @returns {Blob}
 */
function base64ToBlob(dataUrl) {
    const [meta, b64data] = dataUrl.split(',');
    const mimeType = meta.match(/:(.*?);/)[1];

    const binary = atob(b64data);
    const bytes  = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return new Blob([bytes], { type: mimeType });
}
